import { useSelector } from 'react-redux';

import type { Parameters } from '@provider-portal/common/api/gen/DeliveryProviderPortalAuthApi';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { useTargetingParameter } from '@provider-portal/common/services/TargetingService';

/**
 * Duplicate selectors: circular dependencies on import
 * @see file://./../../../app/src/rdx/targetingParameters/reducer.ts
 */
interface TargetingParametersState {
  parameters: Parameters | null;
}

interface FauxGlobalAppState {
  targetingParameters: TargetingParametersState;
}

/**
 * Duplicate interfaces: circular dependencies on import
 * @see file://./../../../app/src/rdx/targetingParameters/selectors.ts
 */
function selectTargetingParameters(state: FauxGlobalAppState): Parameters {
  return state.targetingParameters.parameters || {};
}

export function useTargetingParameters(): Parameters {
  try {
    return useSelector(selectTargetingParameters);
  } catch (error) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      throw new Error('Check Observable variant');
    }

    throw error;
  }
}

export function useTargetingParametersObservable<
  T extends keyof Parameters,
  K extends keyof Parameters[T],
  V extends Parameters[T][K],
  R = Parameters[T][K],
>(targetingEventName: T, testOrFlagName: K, controlValue: V): R {
  const commonAppServices = useCommonAppServices();
  if (commonAppServices === null) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      throw new Error('Check NonObservable variant');
    }

    throw new Error('Context requires a hydrated <Provider>');
  }

  return useTargetingParameter(
    testOrFlagName,
    commonAppServices.targetingService,
    (value) => value ?? controlValue,
    targetingEventName,
  );
}
